/**
 * Liz Luxes Services Cleaning — media library.
 *
 * Photos and videos are hosted in the client's Supabase media bucket. Only the
 * file names are stored here; full-size and thumbnail URLs are derived from the
 * two base paths below (Supabase `object` serves originals, `render` serves
 * resized/compressed thumbnails).
 */

const SUPABASE_ORIGIN = "https://sbdjxsxpkgngbkdabugx.supabase.co/storage/v1";
const BUCKET_PATH = "public/client-media/liz-luxe-services-cleaning-llc";

const IMAGE_OBJECT_BASE = `${SUPABASE_ORIGIN}/object/${BUCKET_PATH}/images`;
const IMAGE_RENDER_BASE = `${SUPABASE_ORIGIN}/render/image/${BUCKET_PATH}/images`;
const VIDEO_OBJECT_BASE = `${SUPABASE_ORIGIN}/object/${BUCKET_PATH}/videos`;

const THUMB_PARAMS = "?width=480&height=480&resize=cover&quality=50";

const imageFiles = [
  "1786168106591-719-43445.jpg",
  "1786168100910-151-43446.jpg",
  "1786167977482-581-46141.jpg",
  "1786167971221-810-46144.jpg",
  "1786167958226-216-46146.jpg",
  "1786167952944-99-46143.jpg",
  "1786167948086-835-46145.jpg",
  "1786167940119-63-45744.jpg",
  "1786167934516-970-45742.jpg",
  "1786167928563-344-45741.jpg",
  "1786167776609-442-47339.jpg",
  "1786167770724-753-47340.jpg",
  "1786167765488-474-47341.jpg",
  "1786167761650-386-47349.jpg",
  "1786167757012-794-47350.jpg",
  "1786167751708-772-47351.jpg",
  "1786167744242-514-47353.jpg",
  "1786167738102-480-47358.jpg",
  "1786167732816-25-47359.jpg",
  "1786167464525-712-47465.jpg",
  "1786167460855-754-47464.jpg",
  "1786167455147-591-47463.jpg",
  "1786167450149-49-47466.jpg",
  "1786167347409-523-48722.jpg",
  "1786167342378-485-48725.jpg",
  "1786167337298-281-48724.jpg",
  "1786167331259-89-48727.jpg",
  "1786167326906-545-48726.jpg",
  "1786167320806-642-48729.jpg",
  "1786167313928-434-48728.jpg",
  "1786167184410-487-54567.jpg",
  "1786167178897-588-54566.jpg",
  "1786167172890-623-54565.jpg",
  "1786167165577-163-54587.jpg",
  "1786167160243-556-54588.jpg",
  "1786167154921-577-54589.jpg",
  "1786167148372-2-54586.jpg",
  "1786167142022-460-54585.jpg",
  "1786167136981-604-54584.jpg",
  "1786167019596-635-59633.jpg",
  "1786167014405-485-59631.jpg",
  "1786167005716-442-59632.jpg",
  "1786166998661-91-59630.jpg",
  "1786166994256-246-59602.jpg",
  "1786166989247-226-59601.jpg",
  "1786166984309-744-59600.jpg",
  "1786166978529-131-59599.jpg",
  "1786166972010-190-59596.jpg",
  "1786166896328-720-54569.jpg",
  "1786166890464-694-54570.jpg",
  "1786166883692-552-54566.jpg",
  "1786166876597-647-54567.jpg",
  "1786166870796-427-54568.jpg",
  "1786166864000-111-54565.jpg",
  "1786166858365-814-54564.jpg",
  "1786166849950-493-54563.jpg",
  "1786166844508-207-54560.jpg",
  "1786166838038-842-54561.jpg",
  "1786166832142-483-54562.jpg",
  "1786166826890-139-54559.jpg",
  "1786166821642-133-54558.jpg",
  "1786166637067-152-68635.jpg",
  "1786166631130-415-68631.jpg",
  "1786166626729-413-68632.jpg",
  "1786166620995-462-68633.jpg",
  "1786166615540-311-68628.jpg",
  "1786166610910-452-68629.jpg",
  "1786166605810-259-68630.jpg",
  "1786166599491-305-68625.jpg",
  "1786166595734-675-68626.jpg",
  "1786166590221-242-68627.jpg",
  "1786166584879-26-68622.jpg",
  "1786166580018-663-68619.jpg",
  "1786166576134-173-68620.jpg",
  "1786166569310-654-68621.jpg",
  "1786166268367-497-71195.jpg",
  "1786166262750-997-71196.jpg",
  "1786166257163-999-71197.jpg",
  "1786166250275-141-71198.jpg",
  "1786166003632-447-71196.jpg",
  "1786165997821-491-71197.jpg",
  "1786165989378-671-71198.jpg",
  "1786165777710-181-71195.jpg",
  "1786165771784-32-71197.jpg",
  "1786165767111-440-71196.jpg",
  "1786165607020-867-72207.jpg",
  "1786165602502-953-72206.jpg",
];

const videoFiles = [
  "1786165652223-987-42163.mp4",
  "1786165506656-854-42160.mp4",
];

export const galleryImages = imageFiles.map((f) => `${IMAGE_OBJECT_BASE}/${f}`);
export const galleryThumbs = imageFiles.map((f) => `${IMAGE_RENDER_BASE}/${f}${THUMB_PARAMS}`);
export const galleryVideos = videoFiles.map((f) => `${VIDEO_OBJECT_BASE}/${f}`);

/** Square, compressed thumbnails used by the gallery grid. */
export const galleryThumb = (index: number) => galleryThumbs[index];

/**
 * Full-resolution originals (~4-5 MB each). Only use these where the user has
 * opted in to loading one, such as the gallery lightbox — never for page
 * imagery. Use `galleryHero` / `galleryCard` instead, which serve the same
 * photos resized through Supabase at roughly 1-3% of the original weight.
 */
export const galleryFull = (index: number) => galleryImages[index];

const rendered = (index: number, params: string) =>
  `${IMAGE_RENDER_BASE}/${imageFiles[index]}?${params}`;

/** Full-bleed page heroes — 1920x1080, ~190 KB. */
export const galleryHero = (index: number) =>
  rendered(index, "width=1920&height=1080&resize=cover&quality=68");

/** Service and blog artwork — 1600x1000, ~155 KB. Doubles as a section hero. */
export const galleryCard = (index: number) =>
  rendered(index, "width=1600&height=1000&resize=cover&quality=70");
